import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  IconButton,
  Stack,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  useTheme,
  alpha,
  Typography,
  Divider,
  Avatar,
  Menu,
  MenuItem
} from '@mui/material';
import {
  TrendingUp,
  Menu as MenuIcon,
  Close,
  AutoAwesome,
  Home,
  Login,
  PersonAdd,
  Dashboard as DashboardIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export function Navigation() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, signOut } = useAuth();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);

  // Handle Scroll for Fixed Header
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      handleUserMenuClose();
      navigate('/');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const handleNavClick = (action: () => void) => {
    setMobileOpen(false);
    window.scrollTo(0, 0);
    action();
  };

  const scrollToFeatures = () => {
    if (location.pathname === '/') {
      document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/');
      // Timeout to allow navigation to complete before scrolling
      setTimeout(() => {
        document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  const getUserInitials = (user: any): string => {
    if (!user?.name) return 'U';
    const nameParts = user.name.trim().split(' ');
    if (nameParts.length === 1) return nameParts[0].charAt(0).toUpperCase();
    return nameParts[0].charAt(0).toUpperCase() + nameParts[nameParts.length - 1].charAt(0).toUpperCase();
  };

  // Default nav items
  const navItems = [
    { label: 'Features', action: scrollToFeatures, icon: <AutoAwesome /> },
    { label: 'Pricing', action: () => navigate('/pricing'), icon: <TrendingUp /> },
    { label: 'About', action: () => navigate('/about'), icon: <Home /> },
  ];

  return (
    <>
      <Box
        component="nav"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1100,
          py: 2, // Fixed padding to avoid layout thrashing
          px: { xs: 2, md: 4 },
          transition: 'background-color 0.3s ease, border-bottom 0.3s ease, backdrop-filter 0.3s ease',
          bgcolor: scrolled ? alpha(theme.palette.background.paper, 0.9) : 'transparent',
          backdropFilter: scrolled ? 'blur(20px)' : 'none',
          // Optimize mobile performance by removing expensive filters if needed, but here we just optimize the transition
          borderBottom: scrolled ? `1px solid ${theme.palette.divider}` : '1px solid transparent',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          willChange: 'background-color, backdrop-filter' // Hint to browser
        }}
      >
        <Box
          component="div"
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }}
          onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); navigate('/'); }}
        >
          <Box component="img" src="/logo.svg" alt="ContentSignal Logo" sx={{ width: 32, height: 32 }} />
          <Typography variant="h6" fontWeight="bold" color="text.primary">
            ContentSignal
          </Typography>
        </Box>

        {/* Desktop Nav */}
        <Stack direction="row" spacing={3} alignItems="center" sx={{ display: { xs: 'none', md: 'flex' } }}>
          {navItems.map((item) => (
            <Button key={item.label} color="inherit" onClick={item.action} sx={{ fontWeight: 500 }}>{item.label}</Button>
          ))}

          {isAuthenticated ? (
            <>
              <Button
                variant="contained"
                onClick={() => navigate('/dashboard')}
                sx={{
                  fontWeight: 600,
                  borderRadius: 50,
                  px: 3,
                  boxShadow: theme.shadows[4]
                }}
                color="primary"
                startIcon={<DashboardIcon />}
              >
                Dashboard
              </Button>
              <IconButton onClick={handleUserMenuOpen} sx={{ ml: 1 }}>
                <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main, fontSize: '0.875rem' }}>
                  {getUserInitials(user)}
                </Avatar>
              </IconButton>
              <Menu
                anchorEl={userMenuAnchor}
                open={Boolean(userMenuAnchor)}
                onClose={handleUserMenuClose}
                PaperProps={{
                  sx: { mt: 1.5, minWidth: 150 }
                }}
              >
                <MenuItem onClick={handleSignOut}>
                  <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                  Sign Out
                </MenuItem>
              </Menu>
            </>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate('/login')}
              sx={{
                borderRadius: 50,
                px: 3,
                fontWeight: 600,
                boxShadow: theme.shadows[4]
              }}
            >
              Sign In
            </Button>
          )}
        </Stack>

        {/* Mobile Menu Icon */}
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="end"
          onClick={handleDrawerToggle}
          sx={{ display: { xs: 'flex', md: 'none' } }}
        >
          <MenuIcon />
        </IconButton>
      </Box>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 280, borderTopLeftRadius: 16, borderBottomLeftRadius: 16 }
        }}
      >
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box component="img" src="/logo.svg" alt="ContentSignal Logo" sx={{ width: 32, height: 32 }} />
            <Typography variant="h6" fontWeight="bold">
              ContentSignal
            </Typography>
          </Box>
          <IconButton onClick={handleDrawerToggle}>
            <Close />
          </IconButton>
        </Box>
        <Divider />
        <List sx={{ px: 2 }}>
          <ListItem disablePadding sx={{ mb: 1 }}>
            <ListItemButton
              onClick={() => handleNavClick(() => navigate('/'))}
              sx={{ borderRadius: 2 }}
            >
              <ListItemIcon><Home color="primary" /></ListItemIcon>
              <ListItemText primary="Home" primaryTypographyProps={{ fontWeight: 600 }} />
            </ListItemButton>
          </ListItem>

          {navItems.map((item) => (
            <ListItem key={item.label} disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                onClick={() => { setMobileOpen(false); item.action(); }}
                sx={{ borderRadius: 2 }}
              >
                <ListItemIcon sx={{ color: 'text.primary' }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 600 }} />
              </ListItemButton>
            </ListItem>
          ))}

          <Divider sx={{ my: 2 }} />

          {isAuthenticated ? (
            <>
              <ListItem disablePadding sx={{ mb: 1 }}>
                <ListItemButton
                  onClick={() => handleNavClick(() => navigate('/dashboard'))}
                  sx={{ borderRadius: 2 }}
                >
                  <ListItemIcon><DashboardIcon /></ListItemIcon>
                  <ListItemText primary="Dashboard" primaryTypographyProps={{ fontWeight: 600 }} />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => { setMobileOpen(false); handleSignOut(); }}
                  sx={{ borderRadius: 2 }}
                >
                  <ListItemIcon><LogoutIcon /></ListItemIcon>
                  <ListItemText primary="Sign Out" primaryTypographyProps={{ fontWeight: 600 }} />
                </ListItemButton>
              </ListItem>
            </>
          ) : (
            <>
              <ListItem disablePadding sx={{ mb: 1 }}>
                <ListItemButton
                  onClick={() => handleNavClick(() => navigate('/login'))}
                  sx={{ borderRadius: 2 }}
                >
                  <ListItemIcon><Login /></ListItemIcon>
                  <ListItemText primary="Login" primaryTypographyProps={{ fontWeight: 600 }} />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleNavClick(() => navigate('/pricing'))}
                  sx={{ borderRadius: 2, bgcolor: theme.palette.primary.main, color: 'white', '&:hover': { bgcolor: theme.palette.primary.dark } }}
                >
                  <ListItemIcon><PersonAdd sx={{ color: 'white' }} /></ListItemIcon>
                  <ListItemText primary="Get Started Free" primaryTypographyProps={{ fontWeight: 700 }} />
                </ListItemButton>
              </ListItem>
            </>
          )}
        </List>
      </Drawer>
    </>
  );
}